import { faArrowLeft, faBook, faFilter, faPen, faTag, faUserGroup, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Chip, Divider, Fade, TextField as MuiTextField, ThemeProvider as MuiThemeProvider } from '@mui/material';
import axios from 'axios';
import { MemreButton, MemreFlexBox, MemreText } from 'components/core';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useGroup } from 'hooks/useGroup';
import { useMutateGroup } from 'hooks/useMutateGroup';
import { usePartnerUserTagTypes } from 'hooks/usePartnerUserTagTypes';
import { Rule, RuleEditor } from 'pages/admin-dashboard/groups/RuleEditor';
import muiTheme from 'pages/learner-dashboard/visulisations/theme';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid } from 'uuid';

// Type definitions
type Props = {
  match: {
    params: {
      groupId: string;
    };
  };
};

interface RuleCondition {
  [operator: string]: {
    tag_type_id: string;
    tag_type_label: string;
    value: string;
  };
}

interface RulesJson {
  AND: RuleCondition[];
}

interface MatchedUser {
  id: string;
  attributes: {
    name: string;
    email: string;
  };
}

// Define theme with colors for styled-components
const styledTheme = {
  colors: {
    primary: Color.ceregoGreen,
    secondary: Color.mainBlue,
    secondaryLight: Color.lightBlue,
    secondaryDark: Color.darkBlue,
    error: Color.red,
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      hint: 'rgba(0, 0, 0, 0.38)',
    },
    smart: {
      bg: 'rgba(0, 150, 136, 0.08)',
      text: 'rgb(0, 150, 136)',
    },
    classic: {
      bg: 'rgba(33, 150, 243, 0.08)',
      text: 'rgb(33, 150, 243)',
    },
    background: '#f5f7fa',
  },
  borderRadius: '8px',
  shadow: {
    light: '0 2px 4px rgba(0, 0, 0, 0.05)',
    medium: '0 4px 8px rgba(0, 0, 0, 0.08)',
  },
};

// Container components
const PageContainer = styled.div`
  width: 100%;
  max-width: 900px;
  min-width: 600px; /* Add minimum width */
  margin: 0 auto;
  padding: 24px 16px;

  @media (max-width: 640px) {
    min-width: auto; /* Let it be responsive on very small screens */
    width: calc(100% - 32px);
  }
`;

const Section = styled(Card)`
  width: 100%;
  border-radius: ${styledTheme.borderRadius};
  box-shadow: ${styledTheme.shadow.light};
  margin-bottom: 20px;
  overflow: visible;
  background: white;
`;

const SectionHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
`;

const SectionContent = styled.div`
  padding: 20px;
`;

// Navigation
const BackButton = styled.button`
  background: none;
  border: none;
  color: ${styledTheme.colors.secondary};
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px 0;
  margin-bottom: 20px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: ${styledTheme.colors.secondaryDark};
  }

  svg {
    margin-right: 8px;
  }
`;

// Title components
const TitleArea = styled.div`
  margin-bottom: 24px;
`;

const GroupTypeTag = styled(Chip)`
  font-weight: 500;
  height: 26px;
  background: ${(props) =>
    props.label === 'Smart Group' ? styledTheme.colors.smart.bg : styledTheme.colors.classic.bg};
  color: ${(props) =>
    props.label === 'Smart Group' ? styledTheme.colors.smart.text : styledTheme.colors.classic.text};
`;

// Rules display
const RulesList = styled.div`
  margin-top: 12px;
`;

const RuleItem = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 8px;
  box-shadow: ${styledTheme.shadow.light};
`;

const RuleTagChip = styled(Chip)`
  font-weight: 500;
  background: ${styledTheme.colors.smart.bg};
  color: ${styledTheme.colors.smart.text};
`;

const RuleValueChip = styled(Chip)`
  font-weight: 500;
  background: #f0f0f0;
  color: rgba(0, 0, 0, 0.7);
`;

// Quick action cards
const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
`;

const QuickActionCard = styled.div`
  background: white;
  border-radius: ${styledTheme.borderRadius};
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${styledTheme.shadow.light};

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${styledTheme.shadow.medium};
  }
`;

const ActionIcon = styled.div`
  width: 46px;
  height: 46px;
  background: ${styledTheme.colors.secondary};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 18px;
`;

const ActionCount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${styledTheme.colors.secondary};
  margin-bottom: 4px;
`;

const ActionLabel = styled.div`
  font-size: 14px;
  color: ${styledTheme.colors.text.secondary};
`;

// User list
const UserListContainer = styled.div`
  margin-top: 16px;
  max-height: 300px;
  overflow-y: auto;
`;

const UserListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 6px;
  background: #f8f9fa;
  margin-bottom: 8px;

  &:hover {
    background: #f0f2f5;
  }
`;

const UserInitials = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${styledTheme.colors.secondary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  margin-right: 12px;
`;

const UserDetails = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const UserEmail = styled.div`
  font-size: 13px;
  color: ${styledTheme.colors.text.secondary};
`;

// Form components
const FormField = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const RuleContainer = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #eee;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;

  svg {
    margin-right: 8px;
    color: ${styledTheme.colors.secondary};
  }
`;

export const GroupDetailPage: React.FC<Props> = ({ match }) => {
  const { groupId } = match.params;
  const { data: group, isLoading, isError, error } = useGroup(groupId);
  const [updateGroup, { isLoading: isUpdating }] = useMutateGroup(groupId);
  const { data: partnerUserTagTypes } = usePartnerUserTagTypes();
  const user = useCurrentUser();
  const partnerId = user?.primaryPartner?.id;
  const history = useHistory();

  // State
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<Rule[]>([]);
  const [matchedUsers, setMatchedUsers] = useState<MatchedUser[]>([]);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Initialize data from group
  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description || '');

      if (group.rulesJson) {
        try {
          const parsedRulesJson: RulesJson = JSON.parse(group.rulesJson);
          const parsedRules: Rule[] = parsedRulesJson.AND.map((condition) => {
            const operator = Object.keys(condition)[0] as '=' | '!=';
            const { tag_type_id, tag_type_label, value } = condition[operator];
            return {
              id: uuid(),
              tagTypeId: tag_type_id,
              tagTypeLabel: tag_type_label,
              operator,
              value,
            };
          });
          setRules(parsedRules);
        } catch (err) {
          console.error('Error parsing rulesJson:', err);
          setRules([]);
        }
      } else {
        setRules([]);
      }
    }
  }, [group]);

  // Get initials for user avatars - with null check
  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: any = {
        id: groupId,
        name,
        description,
      };

      if (group?.rulesJson) {
        const rulesJson = buildRulesJson(rules);
        payload.rules_json = rulesJson;
      }

      await updateGroup({ id: groupId, payload });
      setIsEditing(false);
      setNotification({ show: true, message: 'Group updated successfully', type: 'success' });
    } catch (err) {
      console.error('Update failed:', err);
      setNotification({ show: true, message: 'Failed to update group', type: 'error' });
    }
  };

  const buildRulesJson = (rules: Rule[]) => {
    const conditions = rules.map((rule) => ({
      [rule.operator]: {
        tag_type_id: rule.tagTypeId,
        tag_type_label: rule.tagTypeLabel,
        value: rule.value,
      },
    }));
    return JSON.stringify({ AND: conditions });
  };

  const handleAddRule = () => {
    const newRule: Rule = {
      id: uuid(),
      tagTypeId: '',
      tagTypeLabel: '',
      operator: '=',
      value: '',
    };
    setRules((prevRules) => [...prevRules, newRule]);
  };

  const handleRuleChange = (id: string, updatedFields: Partial<Rule>) => {
    setRules((prevRules) => prevRules.map((rule) => (rule.id === id ? { ...rule, ...updatedFields } : rule)));
  };

  const handleRemoveRule = (id: string) => {
    setRules((prevRules) => prevRules.filter((rule) => rule.id !== id));
  };

  // Preview user matching for smart groups
  const fetchSmartGroupPreview = async (rulesJson: string) => {
    if (!partnerId) return;

    setIsFetchingPreview(true);
    try {
      const response = await axios.post(`/api/v3/partners/${partnerId}/smart_group_preview`, {
        rules_json: rulesJson,
        include: 'user_partner_id',
      });
      const users = response.data.data.map((u: any) => ({
        id: u.id,
        attributes: {
          name: u.attributes.name,
          email: u.attributes.email,
        },
      }));
      setMatchedUsers(users);
    } catch (error) {
      console.error('Error fetching smart group preview', error);
      setMatchedUsers([]);
    } finally {
      setIsFetchingPreview(false);
    }
  };

  // Update preview when rules change
  useEffect(() => {
    if (group?.rulesJson && isEditing && rules.length > 0) {
      const debounceTimeout = setTimeout(() => {
        const rulesJson = buildRulesJson(rules);
        fetchSmartGroupPreview(rulesJson);
      }, 500);
      return () => clearTimeout(debounceTimeout);
    } else {
      setMatchedUsers([]);
    }
  }, [rules, isEditing]);

  const handleCancel = () => {
    if (group) {
      setName(group.name);
      setDescription(group.description || '');

      if (group.rulesJson) {
        try {
          const parsedRulesJson: RulesJson = JSON.parse(group.rulesJson);
          const parsedRules: Rule[] = parsedRulesJson.AND.map((condition) => {
            const operator = Object.keys(condition)[0] as '=' | '!=';
            const { tag_type_id, tag_type_label, value } = condition[operator];
            return {
              id: uuid(),
              tagTypeId: tag_type_id,
              tagTypeLabel: tag_type_label,
              operator,
              value,
            };
          });
          setRules(parsedRules);
        } catch (err) {
          console.error('Error parsing rulesJson:', err);
          setRules([]);
        }
      } else {
        setRules([]);
      }
    }
    setIsEditing(false);
  };

  // Loading state
  if (isLoading)
    return (
      <ThemeProvider theme={styledTheme}>
        <MuiThemeProvider theme={muiTheme}>
          <PageContainer>
            <MemreFlexBox direction="column" align="center" justify="center" sx={{ height: '300px' }}>
              <MemreText variant="body1">Loading group details...</MemreText>
            </MemreFlexBox>
          </PageContainer>
        </MuiThemeProvider>
      </ThemeProvider>
    );

  // Error state
  if (isError) {
    console.error(error);
    return (
      <ThemeProvider theme={styledTheme}>
        <MuiThemeProvider theme={muiTheme}>
          <PageContainer>
            <BackButton onClick={() => history.push('/nav/v4/admin/groups')}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Groups
            </BackButton>

            <MemreFlexBox direction="column" align="center" justify="center" sx={{ height: '300px' }}>
              <MemreText variant="body1" sx={{ marginBottom: '16px' }}>
                Error loading group details
              </MemreText>
              <MemreButton
                text="Return to Groups"
                variant="contained"
                color="secondary"
                onClick={() => history.push('/nav/v4/admin/groups')}
              />
            </MemreFlexBox>
          </PageContainer>
        </MuiThemeProvider>
      </ThemeProvider>
    );
  }

  const isSmartGroup = !!group.rulesJson;

  return (
    <ThemeProvider theme={styledTheme}>
      <MuiThemeProvider theme={muiTheme}>
        <PageContainer>
          <BackButton onClick={() => history.push('/nav/v4/admin/groups')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Groups
          </BackButton>

          {!isEditing ? (
            // View Mode
            <Fade in={!isEditing} timeout={300}>
              <div>
                <TitleArea>
                  <MemreFlexBox direction="row" align="center" justify="space-between" sx={{ marginBottom: '16px' }}>
                    <MemreFlexBox direction="row" align="center" gap={1.5}>
                      <MemreText variant="h4" sx={{ fontWeight: 600, margin: 0 }}>
                        {group.name}
                      </MemreText>
                      <GroupTypeTag label={isSmartGroup ? 'Smart Group' : 'Classic Group'} size="small" />
                    </MemreFlexBox>

                    <MemreButton
                      text="Edit Group"
                      variant="contained"
                      color="secondary"
                      onClick={() => setIsEditing(true)}
                      iconName="edit"
                      size="small"
                    />
                  </MemreFlexBox>

                  {group.description && (
                    <MemreText variant="body1" sx={{ color: 'rgba(0,0,0,0.7)' }}>
                      {group.description}
                    </MemreText>
                  )}
                </TitleArea>

                {/* Smart Group Rules */}
                {isSmartGroup && rules.length > 0 && (
                  <Section>
                    <SectionHeader>
                      <SectionTitle>
                        <FontAwesomeIcon icon={faFilter} />
                        Group Rules
                      </SectionTitle>
                    </SectionHeader>

                    <SectionContent>
                      <MemreText variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', marginBottom: '12px' }}>
                        Users matching the following criteria are automatically added to this group:
                      </MemreText>

                      <RulesList>
                        {rules.map((rule) => {
                          const operatorText = rule.operator === '=' ? 'is' : 'is not';
                          return (
                            <RuleItem key={rule.id}>
                              <RuleTagChip label={rule.tagTypeLabel} size="small" />
                              <div style={{ margin: '0 10px', fontWeight: 500, color: 'rgba(0,0,0,0.6)' }}>
                                {operatorText}
                              </div>
                              <RuleValueChip label={rule.value} size="small" />
                            </RuleItem>
                          );
                        })}
                      </RulesList>
                    </SectionContent>
                  </Section>
                )}

                {/* Quick Actions */}
                <Section>
                  <SectionHeader>
                    <SectionTitle>
                      <FontAwesomeIcon icon={faTag} />
                      Quick Actions
                    </SectionTitle>
                  </SectionHeader>

                  <SectionContent>
                    <QuickActionsGrid>
                      <QuickActionCard onClick={() => history.push(`/nav/v4/admin/groups/${groupId}/members`)}>
                        <ActionIcon>
                          <FontAwesomeIcon icon={faUsers} />
                        </ActionIcon>
                        <ActionCount>{group.membersCount}</ActionCount>
                        <ActionLabel>{group.membersCount === 1 ? 'Member' : 'Members'}</ActionLabel>
                      </QuickActionCard>

                      <QuickActionCard onClick={() => history.push(`/nav/v4/admin/groups/${groupId}/courses`)}>
                        <ActionIcon>
                          <FontAwesomeIcon icon={faBook} />
                        </ActionIcon>
                        <ActionCount>{group.coursesCount}</ActionCount>
                        <ActionLabel>{group.coursesCount === 1 ? 'Course' : 'Courses'}</ActionLabel>
                      </QuickActionCard>
                    </QuickActionsGrid>
                  </SectionContent>
                </Section>
              </div>
            </Fade>
          ) : (
            // Edit Mode
            <Fade in={isEditing} timeout={300}>
              <form onSubmit={handleSubmit}>
                <TitleArea>
                  <MemreText variant="h4" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                    Edit Group
                  </MemreText>
                  <MemreText variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                    Update the information and settings for this group
                  </MemreText>
                </TitleArea>

                <Section>
                  <SectionHeader>
                    <SectionTitle>
                      <FontAwesomeIcon icon={faPen} />
                      Group Information
                    </SectionTitle>
                  </SectionHeader>

                  <SectionContent>
                    <FormField>
                      <FormLabel>Name</FormLabel>
                      <MuiTextField
                        variant="outlined"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        disabled={isUpdating}
                        size="small"
                        color="secondary"
                        fullWidth
                        required
                      />
                    </FormField>

                    <FormField>
                      <FormLabel>Description</FormLabel>
                      <MuiTextField
                        color="secondary"
                        variant="outlined"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        disabled={isUpdating}
                        size="small"
                        placeholder="Describe the purpose of this group..."
                        multiline
                        fullWidth
                      />
                    </FormField>
                  </SectionContent>
                </Section>

                {/* Smart Group Rules */}
                {isSmartGroup && (
                  <Section>
                    <SectionHeader>
                      <SectionTitle>
                        <FontAwesomeIcon icon={faFilter} />
                        Group Rules
                      </SectionTitle>
                    </SectionHeader>

                    <SectionContent>
                      <MemreText variant="body2" sx={{ marginBottom: '16px', color: 'rgba(0,0,0,0.6)' }}>
                        Define rules to automatically include users in this group based on their attributes.
                      </MemreText>

                      {rules.map((rule) => (
                        <RuleContainer key={rule.id}>
                          <RuleEditor
                            rule={rule}
                            partnerUserTagTypes={partnerUserTagTypes || []}
                            onRuleChange={handleRuleChange}
                            onRemoveRule={handleRemoveRule}
                          />
                        </RuleContainer>
                      ))}

                      <MemreButton
                        text="Add Rule"
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddRule}
                        iconName="plus"
                        size="small"
                        sx={{ marginTop: '8px' }}
                      />

                      {/* Preview Matching Users */}
                      {rules.length > 0 && (
                        <div style={{ marginTop: '24px' }}>
                          <Divider sx={{ mb: 3 }} />

                          <MemreText
                            variant="subtitle2"
                            sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}
                          >
                            <FontAwesomeIcon icon={faUserGroup} style={{ marginRight: '8px' }} />
                            {isFetchingPreview
                              ? 'Finding matching users...'
                              : `Matching Users (${matchedUsers.length})`}
                          </MemreText>

                          {isFetchingPreview ? (
                            <MemreText variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                              Loading preview...
                            </MemreText>
                          ) : matchedUsers.length > 0 ? (
                            <UserListContainer>
                              {matchedUsers.map((user) => (
                                <UserListItem key={user.id}>
                                  <UserInitials>{getInitials(user.attributes.name)}</UserInitials>
                                  <UserDetails>
                                    <UserName>{user.attributes.name}</UserName>
                                    <UserEmail>{user.attributes.email}</UserEmail>
                                  </UserDetails>
                                </UserListItem>
                              ))}
                            </UserListContainer>
                          ) : (
                            <MemreText
                              variant="body2"
                              sx={{ color: 'rgba(0,0,0,0.6)', fontStyle: 'italic', padding: '12px 0' }}
                            >
                              No users match the current rules.
                            </MemreText>
                          )}
                        </div>
                      )}
                    </SectionContent>
                  </Section>
                )}

                <ButtonGroup>
                  <MemreButton
                    text="Cancel"
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancel}
                    disabled={isUpdating}
                  />
                  <MemreButton
                    text={isUpdating ? 'Saving...' : 'Save Changes'}
                    variant="contained"
                    color="secondary"
                    onClick={handleSubmit}
                    disabled={isUpdating}
                  />
                </ButtonGroup>
              </form>
            </Fade>
          )}
        </PageContainer>
      </MuiThemeProvider>
    </ThemeProvider>
  );
};
